/**
 * The rare moves on a task, behind one "More" — on a list row, a board card and the
 * task page.
 *
 * Two lines of the owner's list meet here: "put less-common actions in an overflow
 * menu" for the row, and "Cancel and other rare actions under More" for the detail,
 * where they replace "the crowded top-right action row". One component rather than
 * two, for the same reason `facetsToTaskFilters` is one function — a row and a page
 * that offered different rare moves would be two answers about the same task.
 *
 * **What is in it is the state machine's**, not a list written here: `taskMachine`
 * says which moves exist from a status, `canMutateWorkOf` says whether they are this
 * reader's to make, and the menu draws what survives both. Comment and Log time are
 * always there — they belong to anybody who was in the room, and the server takes
 * late time on a closed task by design.
 *
 * Each item opens its own `ActionDialog` in controlled mode, because a dialog raised
 * from inside a popover has to outlive the popover that raised it: Radix unmounts the
 * menu's content on close, and an uncontrolled dialog would go with it.
 */

import { taskMachine, type TaskView } from '@fareast/shared'
import { useState } from 'react'

import type { StudioActionName } from '../../api/studio.js'
import { usePrincipal } from '../../auth/principal.js'
import { Button } from '../portal/Button.js'
import { Popover } from '../portal/Overlay.js'
import { ActionDialog, useFocusBackAfterWaiting } from './ActionDialog.js'
import { DIALOG_MOVES, canMutateWorkOf, moveLabel, moveWindowOf } from './taskWords.js'

type MoreItem = {
  readonly key: string
  readonly action: StudioActionName
  /** The commit's own word — the dialog's submit button. Never an ellipsis. */
  readonly label: string
  /** What the entry says, when opening a window is not what the commit says. */
  readonly menuLabel?: string | undefined
  readonly title: string
  /** True when the move ends the task: a red word, and a window that names it. */
  readonly danger?: boolean | undefined
  /** The dismiss control's words, when "Close" would mean something else here. */
  readonly dismissLabel?: string | undefined
  /**
   * True for a move — Block, Give it back, Cancel — which is made from who holds the
   * task and where it stands (`taskWords.ts` `moveOf`). A comment and a log of time
   * are said about a task whoever has it, and their tools take no such condition.
   */
  readonly move?: true | undefined
}

/**
 * Every entry here opens a window, so every entry ends in an ellipsis — the
 * convention, stated once: **a menu item that opens a dialog gets it, a submit
 * button never does**. `label` is what the button inside the window says.
 */
function entryWords(item: MoreItem): string {
  return item.menuLabel ?? `${item.label}…`
}

export type TaskMoreMenuProps = {
  readonly task: TaskView
  /** `sm` on a row or a card; the task page's own row draws it full size. */
  readonly size?: 'sm' | undefined
  /**
   * Where it is drawn, which decides what "rare" means.
   *
   * On a **row** a comment and a log of time are rare, because the row has neither a
   * thread nor a time table. On the **page** they are not rare at all — they sit
   * beside Thread and beside Time logged, which is the owner's own placement (#20) —
   * so the menu there holds only what the page has nowhere else to put.
   */
  readonly surface?: 'row' | 'page' | undefined
  /**
   * True while a move of the row's own is on its way. A window opened then is opened
   * over a row that has not been read again, and its condition (`ActionForm`
   * `useOpenedFrom`) is the state the reader has just changed.
   */
  readonly disabled?: boolean | undefined
}

export function TaskMoreMenu({ task, size, surface = 'row', disabled }: TaskMoreMenuProps) {
  const principal = usePrincipal()
  const [open, setOpen] = useState(false)
  const [chosen, setChosen] = useState<MoreItem | null>(null)
  /*
   * More waits while a move of the reader's own on its task is on its way
   * (`disabled`), and `s` on the task page is pressed with the cursor wherever it is:
   * on More, it was taken off and given to nobody.
   */
  const moreId = useFocusBackAfterWaiting(disabled)

  const events = taskMachine.eventsFrom(task.status)
  const mayWork = canMutateWorkOf(task, principal.kind === 'staff_user' ? principal.userId : null)

  const items: MoreItem[] =
    surface === 'row'
      ? [
          {
            key: 'comment',
            action: 'tasks_comment',
            label: 'Comment',
            title: 'Say something on this task',
          },
          {
            key: 'log',
            action: 'tasks_log_time',
            label: 'Log time',
            title: 'Log time against this task',
          },
        ]
      : []

  // The task page draws Block and Give it back beside Status, so its More does not repeat them (#20).
  const beside = surface === 'page'
  const block = DIALOG_MOVES.block
  if (!beside && mayWork && events.includes('block') && block !== undefined) {
    items.push({
      key: 'block',
      action: block.action,
      label: block.label,
      title: block.title,
      move: true,
      /* Not destructive — a blocked task is still a task — but it is about *this* one. */
    })
  }
  /*
   * Give it back and Cancel read their words off `DIALOG_MOVES`, the table the task
   * page's own lifecycle row draws from. A second "Why is it not being done?" written
   * here would be the same question asked twice, differently.
   */
  const release = DIALOG_MOVES.release
  if (!beside && mayWork && events.includes('release') && release !== undefined) {
    items.push({
      key: 'release',
      action: release.action,
      label: release.label,
      title: release.title,
      move: true,
    })
  }
  const cancel = DIALOG_MOVES.cancel
  if (events.includes('cancel') && cancel !== undefined) {
    items.push({
      key: 'cancel',
      action: cancel.action,
      /* The entry names the task, the button commits: "Cancel this task…" / "Cancel it". */
      label: cancel.label,
      menuLabel: `${moveLabel('cancel')}…`,
      title: cancel.title,
      move: true,
      danger: true,
      /*
       * Not "Close". Closing a task is what Mark done means in this product, so the
       * way out of the window that ends one has to say what it leaves behind.
       */
      dismissLabel: 'Keep the task',
    })
  }

  /*
   * A menu with nothing in it is not a menu.
   *
   * On a **row** this cannot happen — comment and log time belong to anybody who was
   * in the room — but on the **page** the items are the machine's alone, and
   * `taskMachine` offers nothing at all from `done` or `cancelled`. So a finished
   * task drew a More button over an empty panel, which is the fold the task page's
   * own `MoreControls` used to refuse by returning null for a terminal status. The
   * guard belongs here rather than back in that caller: it is the menu that knows
   * whether it has anything to say.
   */
  if (items.length === 0) return null

  return (
    <>
      <Popover
        menu
        open={open}
        onOpenChange={setOpen}
        aria-label={`More moves for ${task.ref}`}
        trigger={
          <Button id={moreId} size={size} aria-label={`More for ${task.ref}`} disabled={disabled}>
            More
          </Button>
        }
      >
        {/*
          Roving focus, by hand (correction round 1).
          A Radix `Popover` closes on Esc and returns focus to its trigger, but it
          does not walk its own children — that is `DropdownMenu`'s job, and a second
          Radix package is a dependency this round may not add. Nine lines here buy
          the same arrows.
        */}
        {/*
          `role="menu"` is on the popover's own content element (`Overlay.tsx`), not
          here: Radix gives every popover `role="dialog"`, and a menu announced as a
          dialog is a dialog with four unexplained buttons in it. This div is the
          roving focus and nothing else.
        */}
        <div
          onKeyDown={(event) => {
            const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0
            if (step === 0 && event.key !== 'Home' && event.key !== 'End') return
            event.preventDefault()
            const all = [...event.currentTarget.querySelectorAll('button')]
            const here = all.indexOf(document.activeElement as HTMLButtonElement)
            const next =
              event.key === 'Home'
                ? 0
                : event.key === 'End'
                  ? all.length - 1
                  : (here + step + all.length) % all.length
            all[next]?.focus()
          }}
        >
          {items.map((item) => (
            /*
              A menu entry, not a button that happens to be inside a menu.

              `.menu__item` is the one shape `portal.css` gives a list of moves —
              full width, one to a line — and `role="menuitem"` is what makes the
              `role="menu"` above it true. Drawn as a `Button`, the entries were
              inline-flex ghosts sitting side by side in a 379px strip, which is
              neither a menu to look at nor a menu to a screen reader.
            */
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className={item.danger === true ? 'menu__item menu__item--danger' : 'menu__item'}
              onClick={() => {
                setOpen(false)
                setChosen(item)
              }}
            >
              {entryWords(item)}
            </button>
          ))}
        </div>
      </Popover>

      {chosen === null ? null : (
        <ActionDialog
          action={chosen.action}
          label={chosen.label}
          title={chosen.title}
          /*
           * Which task, in the window's own header. A dialog raised from a row on a
           * board of thirty is a dialog with nothing behind it to read.
           */
          about={
            <>
              {task.ref} · {task.title}
            </>
          }
          dismissLabel={chosen.dismissLabel}
          submitLabel={chosen.label}
          submitVariant={chosen.danger === true ? 'danger' : undefined}
          size="sm"
          {...(chosen.move === true ? moveWindowOf(task) : { fixed: { task_ref: task.ref } })}
          open
          onOpenChange={(next) => {
            if (!next) setChosen(null)
          }}
          onDone={() => {
            setChosen(null)
          }}
        />
      )}
    </>
  )
}
